import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AccessPolicyUpdate,
  clearMcpAccessPolicy,
  getMcpAccessPolicy,
  getMcpRaw,
  getMcpsBaseAccessPolicy,
  updateMcpAccessPolicy,
  updateMcpsBaseAccessPolicy,
  updateMcpServer,
} from "@/api/admin-api"
import { api } from "@/lib/api-client"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// MCP Server Management
export function useAdminAgentMcpsQuery(agentName: string, enabled = true) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "mcps"],
    queryFn: async () => {
      const response = await api.get(`/admin/agents/${agentName}/mcps`)
      const data = response.data || response
      return Array.isArray(data) ? data : []
    },
    enabled: enabled && !!agentName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useAdminMcpsBaseAccessPolicyQuery(agentName: string, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "mcps", "base", "access"],
    queryFn: () => getMcpsBaseAccessPolicy(agentName),
    enabled: enabled && !!agentName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminMcpAccessPolicyQuery(
  agentName: string,
  label: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "mcps", label, "access"],
    queryFn: () => getMcpAccessPolicy(agentName, label ?? ""),
    enabled: enabled && !!agentName && !!label,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateMcpAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      label,
      policy,
    }: {
      agentName: string
      label: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateMcpAccessPolicy(agentName, label, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps", variables.label, "access"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps"],
      })
    },
  })
}

export function useUpdateMcpsBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      policy,
    }: {
      agentName: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateMcpsBaseAccessPolicy(agentName, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps", "base", "access"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps"],
      })
    },
  })
}

export function useClearMcpAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ agentName, label }: { agentName: string; label: string }) => {
      return await clearMcpAccessPolicy(agentName, label)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps", variables.label, "access"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps"],
      })
    },
  })
}

export function useAddMcpServerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      config,
    }: {
      agentName: string
      config: {
        label: string
        url: string
        allowed_tools?: string[] | null
        require_approval?: string
        enabled?: boolean
      }
    }) => {
      const response = await api.post(`/admin/agents/${agentName}/mcps`, config)
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to add MCP server")
      }
      return data
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps"],
      })
    },
  })
}

export function useUpdateMcpStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      label,
      enabled,
    }: {
      agentName: string
      label: string
      enabled: boolean
    }) => {
      const response = await api.put(
        `/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}/status`,
        { enabled }
      )
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update MCP status")
      }
      return data
    },
    onMutate: async ({ agentName, label, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "agents", agentName, "mcps"] })

      const previousMcps = queryClient.getQueryData<Array<{ label: string; enabled: boolean }>>([
        "admin",
        "agents",
        agentName,
        "mcps",
      ])

      if (previousMcps) {
        queryClient.setQueryData(
          ["admin", "agents", agentName, "mcps"],
          previousMcps.map((mcp) => (mcp.label === label ? { ...mcp, enabled } : mcp))
        )
      }

      return { previousMcps, agentName }
    },
    onError: (err, variables, context) => {
      if (context?.previousMcps) {
        queryClient.setQueryData(
          ["admin", "agents", context.agentName, "mcps"],
          context.previousMcps
        )
      }
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps"],
      })
    },
  })
}

export function useDeleteMcpServerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ agentName, label }: { agentName: string; label: string }) => {
      const response = await api.delete(
        `/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}`
      )
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to delete MCP server")
      }
      return data
    },
    onMutate: async ({ agentName, label }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "agents", agentName, "mcps"] })

      const previousMcps = queryClient.getQueryData<Array<{ label: string }>>([
        "admin",
        "agents",
        agentName,
        "mcps",
      ])

      if (previousMcps) {
        queryClient.setQueryData(
          ["admin", "agents", agentName, "mcps"],
          previousMcps.filter((mcp) => mcp.label !== label)
        )
      }

      return { previousMcps, agentName }
    },
    onError: (err, variables, context) => {
      if (context?.previousMcps) {
        queryClient.setQueryData(
          ["admin", "agents", context.agentName, "mcps"],
          context.previousMcps
        )
      }
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "mcps"],
      })
    },
  })
}

// =============================================================================
// MCP server update (the new PUT endpoint, used by edit-mcp-dialog)
// =============================================================================

type UpdateMcpArgs = {
  label: string
  // Matches `UpdateMcpServerArgs["updates"]` in admin-api.ts: the Raw tab
  // forwards arbitrary forward-compatible fields, so an index signature is
  // required for TypeScript to surface real mismatches at the hook layer.
  updates: {
    url?: string
    allowed_tools?: string[] | null
    require_approval?: string
    headers?: Record<string, string>
    enabled?: boolean
    [key: string]: unknown
  }
  source?: string
  reason?: string
  /** Required when source === "raw"; ignored otherwise. */
  expectedHash?: string
}

export function useUpdateMcpServerMutation(agentName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ label, updates, source, reason, expectedHash }: UpdateMcpArgs) =>
      updateMcpServer({ agentName, label, updates, source, reason, expectedHash }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "mcps", agentName] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "agent", agentName] })
    },
  })
}

export function useMcpRawQuery(agentName: string, label: string | null, enabled = true) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "mcps", label, "raw"],
    queryFn: () => getMcpRaw(agentName, label as string),
    enabled: enabled && !!label,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}
